import React, { useCallback, useEffect, useState } from 'react';
import { TextField } from '@framework-controls/core';
import classNames from 'classnames';
import { strings } from 'src/localization';
import css from './VehicleCard.module.scss';
import { PhotoList, SpecData, VehicleRec, ascFormEngagementInput } from 'src/models';
import { ViewMode } from 'src/enums';
import VehicleModelImages from '../VehicleImages/VehicleModelImages';
import { ascFormEngagement } from 'src/helpers/EventHelper/EventHelper';
import { buildApptData, sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState } from 'src/store/states';

export interface IProps {
    vehicle: VehicleRec;
    // The selected flag is used for styling
    selected?: boolean;
    // The clickVehicle prop will receive a string with the id of the vehicle clicked
    clickVehicle?: (id: string, e: any) => void;
    updateMileageState?: (id: string, mileage: number) => void;
    summary?: boolean;
    menu?: boolean;
    viewMode?: ViewMode;
    vehPhotoList?: PhotoList;
    theme: string;
    newVehicleMileage: string;
    prevMileage: number,
    apptMileage: number,
    cancellingAppointment: boolean,
    Itype: string | undefined;
    TMNAMileage?: number,
    locale?: string;
    appointment: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    getFieldRef?: (el: HTMLElement) => void;
}

const VehicleCard = (props: IProps) => {
    // maxLength of mileage input + the comma
    const maxLength = 7;
    const { vehicle } = props;

    const [prevMileage, setPrevMileage] = useState(props.newVehicleMileage ? props.newVehicleMileage.replace(/\D/g, '') :
        (vehicle.prevMileage === -1 ? (props.TMNAMileage && props.TMNAMileage > props.prevMileage) ?
            props.TMNAMileage.toLocaleString() :
            props.prevMileage?.toLocaleString() : (props.apptMileage !== 0 ?
            props.apptMileage?.toLocaleString() : vehicle.prevMileage?.toLocaleString())));

    if (prevMileage === 'NaN') {
        setPrevMileage('0');
    }

    const [milHelpText, setMilHelpText] = useState('');
    const [milHelpError, setMilHelpError] = useState(false);
    const [onLoad, setOnLoad] = useState<boolean>(true);

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    useEffect(() => {
        const mileageNum = parseFloat(prevMileage.replace(/\D/g, '')) || 0;
        if (mileageNum < props.prevMileage && props.prevMileage > 0) {
            setMilHelpText(strings.UpdateMilLess);
            setMilHelpError(true);
        } else {
            setMilHelpText('');
            setMilHelpError(false);
        }
    }, [prevMileage, props.prevMileage]);

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    let yearColorStyle = '';


    if (vehicle.year) {
        yearColorStyle = yearColorStyle + vehicle.year;
    }
    if (vehicle.color) {
        if (vehicle.year) {
            yearColorStyle = yearColorStyle + ' | ' + vehicle.color;
        } else {
            yearColorStyle = yearColorStyle + vehicle.color;
        }
    }
    if (vehicle.style) {
        if (vehicle.year) {
            yearColorStyle = yearColorStyle + ' | ' + vehicle.style;
        } else {
            yearColorStyle = yearColorStyle + vehicle.style;
        }
    }

    // onChangeHandler takes the value typed in by the user and validates the length,
    // as well as parsing and reformatting the number.
    const onChangeHandler = (mileage: string) => {
        if (mileage.length <= maxLength) {
            let mileageNum = parseFloat(mileage.replace(/\D/g, ''));
            if (isNaN(mileageNum)) {
                mileageNum = 0;
            }
            setPrevMileage(mileageNum.toLocaleString());
        }
    };

    // onBlurHandler takes the value typed in by the user and validates the length,
    // as well as parsing mileage to a number.
    const onBlurHandler = useCallback((mileage: string, fieldEdit: boolean) => {
        if (mileage.length <= maxLength && props.updateMileageState) {
            let mileageNum = parseFloat(mileage.replace(/\D/g, ''));
            if (isNaN(mileageNum)) {
                mileageNum = 0;
            }
            props.updateMileageState(vehicle.id, mileageNum);
            if (mileageNum >= props.prevMileage) {
                setPrevMileage(mileageNum.toLocaleString());
                setMilHelpText('');
                setMilHelpError(false);
            } else if (props.prevMileage > 0) {
                setMilHelpText(strings.UpdateMilLess);
                setMilHelpError(true);
            }
        }
        if (fieldEdit) {
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'service',
                commType: 'form',
                commStatus: 'engage',
                elementText: strings.CurrentMileage,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'service_appointment',
            };
            ascFormEngagement(ascFormEngagementInp, [props.vehicle]);

            if (props.appointment && isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceReviewStart', buildApptData(props.sessionId ?? '',
                    getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
            }
        }
    }, [props.prevMileage, props.updateMileageState, vehicle.id, props.vehicle, props.appointment,
        isSDAnalyticsEnabled, props.sessionId, props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
        props.lexusMCLaunched, props.lexusTDMCLaunched]);

    // updates the Vehicle Card on the summary screen if accessing on an Edit
    useEffect(() => {
        if (props.apptMileage !== 0 && onLoad && props.summary) {
            const updatedMileage = props.TMNAMileage && props.TMNAMileage > props.apptMileage ? props.TMNAMileage :
                props.apptMileage;
            setPrevMileage(updatedMileage.toString());
            onBlurHandler(updatedMileage.toString(), false);
            setOnLoad(false);
        }
    }, [onBlurHandler, props.apptMileage, onLoad, props.summary, prevMileage, props.newVehicleMileage, props.prevMileage,
        vehicle.prevMileage, props.TMNAMileage, props.vehicle]);

    // clear prevMileage on cancellingAppointment=true
    useEffect(() => {
        if (props.cancellingAppointment){
            setPrevMileage('');
        }
    }, [props.cancellingAppointment]);

    // If selected include the selected class for styling
    let classes = classNames(
        css.vehicle,
        {
            [css.selected]: (props.selected && !props.summary),
            [css.fullMobileBorder]: props.summary,
            [css.menuBg]: props.menu,
            [css.sumVehicle]: props.summary,
            [css.selectedBMW]: (props.selected && props.theme==='bmw'),
            [css.selectedNissan]: (props.selected && props.theme === 'nissan'),
            [css.selectedMB]: (props.selected && props.theme==='mercedesBenz'),
            [css.selectedMazda]: (props.selected && props.theme === 'mazda'),
            [css.selectedAcura]: (props.selected && props.theme === 'acura'),
            [css.sumVehicleBMW]: (props.summary && props.theme==='bmw'),
            [css.sumVehicleCA]: (props.summary && props.theme==='cadillac'),
            [css.sumVehicleIN]: (props.summary && props.theme==='infiniti'),
        },
    );

    if (props.menu) {
        classes = classNames(
            css.menuVeh,
            {
                [css.selected]: (props.selected && !props.summary),
                [css.fullMobileBorder]: props.summary,
                [css.menuBg]: props.menu,
                [css.menuVehHonda]: props.theme === 'honda',
                [css.menuBgBMW]: props.theme === 'bmw',
            },
        );
    }

    const vinTextStyles = classNames(
        css.vinText,
        {
            [css.vinTextNissan]: props.theme === 'nissan',
        },
    );

    const prevMileageStringStyles = classNames(
        css.prevMileageString,
        {
            [css.fontWeightBaseText]: props.theme === 'ford' || props.theme === 'infiniti',
            [css.colorIsVehicleDisplayMakeModel ]: props.theme === 'nissan',
        },
    );

    // if the user pressed Enter (32) or spacebar (13) when focus is on a pane
    // then we want to act as if they had done a mouse click the pane
    const keyFunction = useCallback((e) => {
        if ((e.keyCode === 32 || e.keyCode === 13) && props.clickVehicle) {
            props.clickVehicle(vehicle.id, e);
        }
    }, [props.clickVehicle, vehicle]);

    let vinDiv: null | JSX.Element = props.viewMode === ViewMode.CustAcct ?
        (<div className={vinTextStyles}>{vehicle.vin}</div>) : null;

    if (props.viewMode === ViewMode.Appt) {
        vinDiv = (<div className={vinTextStyles}>{vehicle.vin} {vehicle.licenseNo ? ` | ${vehicle.licenseNo}` : ''}</div>);
    }

    const previousMileageDiv: null | JSX.Element = (vehicle.prevRoDate && !props.summary) ?
        (<div className={prevMileageStringStyles}>
            {prevMileage + strings.formatString(strings.PreviousMileage, vehicle.prevRoDate).toString()}
        </div>) : null;

    const clickVehicleHandler = useCallback((vehId: string, e: any) => {
        if (props.clickVehicle) {
            props.clickVehicle(vehId, e);
        }
    }, [props.clickVehicle]);

    const clickMileageHandler = useCallback((e: React.MouseEvent) => {
        // Stop propogation if the user clicks the textfield in CustAcct mode
        // This is in order to allow the user to change the vehicle mileage without proceeding
        if (props.viewMode === ViewMode.CustAcct) {
            e.stopPropagation();
        }
    }, [props.viewMode]);


    const litDivClasses = classNames(
        css.litDiv,
        {
            [css.litDivAudi]: (props.theme === 'audi'),
        },
    );

    const currentMileageDiv: null | JSX.Element = (props.summary) ?
        (<div className={css.textField}>
            <div className={litDivClasses} id={'mileageDiv'} onClick={clickMileageHandler}>
                <TextField
                    value={prevMileage.toLocaleString()}
                    id={'prevMileage' + vehicle.id}
                    size='auto'
                    type='tel'
                    disabled={props.menu}
                    onChange={(_, value) => onChangeHandler(value)}
                    onBlur={(_, value) => onBlurHandler(value, true)}
                    helperText={milHelpText}
                    error={milHelpError}
                    getFieldRef={props.getFieldRef}
                />
            </div>
            <div className={prevMileageStringStyles}>{strings.CurrentMileage}</div>
        </div>) : null;

    let displayMake = vehicle.make ? vehicle.make : '';
    const displayModel = vehicle.model ? vehicle.model : '';
    if (props.vehicle.makeCode === '2I') {
        displayMake = 'Scion';
    }
    if (props.vehicle.make === 'Ford Canada') {
        displayMake = 'Ford';
    }
    if (props.vehicle.make === 'Chrysler Canada') {
        displayMake = 'Chrysler';
    }
    if (props.vehicle.make === 'Vw Canada') {
        displayMake = 'Volkswagen';
    }
    if (props.vehicle.make === 'Bmwi') {
        displayMake = 'BMW';
    }
    if (props.vehicle.make === 'Honda Mtc/pp/pe') {
        displayMake = 'Honda';
    }
    if (props.vehicle.make === 'Cummins Mid-canada') {
        displayMake = 'Cummins';
    }
    if (props.vehicle.make === 'Discover Canada') {
        displayMake = 'Discover';
    }

    const cardClass = classNames(
        css.h4Card,
        {
            [css.fontWeightBaseText]: props.theme === 'honda' || props.theme === 'bmw' || props.theme === 'ford' ||
            (props.theme === 'mercedesBenz' && props.summary),
            [css.h4CardIN]: props.theme === 'infiniti',
            [css.colorIsVehicleDisplayMakeModel]: props.theme === 'nissan',
            [css.fontFamilyBaseFamily]: props.theme === 'mercedesBenz',
            [css.h4CardMazda]: props.theme === 'mazda',
            [css.h4CardAcura]: props.theme === 'acura',
        },
    );

    const smallTextStyles = classNames(
        css.smallText,
        {
            [css.fontWeightBaseText]: props.theme === 'honda' || props.theme === 'mercedesBenz' ||
            props.theme === 'bmw' || props.theme === 'ford',
            [css.smallTextIN]: props.theme === 'infiniti',
            [css.colorIsVehicleDisplayMakeModel]: props.theme === 'nissan',
            [css.smallTextMazda]: props.theme === 'mazda',
            [css.smallTextAcura]: props.theme === 'acura',
        },
    );

    return (
        <div id={'VehicleCard' + vehicle.id}
            className={classes}
            onClick={(e) => clickVehicleHandler(vehicle.id, e)}
            onKeyDown={(e) => keyFunction(e)}
            tabIndex={0}>
            <VehicleModelImages
                year={vehicle.year}
                make={vehicle.make}
                model={vehicle.model}
                type='thumb'
                className={css.vehicleImg}
                Itype={props?.Itype}
            />
            <div className={css.vehicleData}>
                <h4 className={css.h4VINCard}>
                    <div className={cardClass}>{displayMake} {displayModel}</div>
                </h4>
                <div className={smallTextStyles}>{yearColorStyle}</div>
                {vinDiv}
                {previousMileageDiv}
                {currentMileageDiv}
            </div>
        </div>
    );
};

export default VehicleCard;
